<script setup>
/**
 * ImmersivePromoItemsModal
 * The multi-item promotion "sub modal": a bottom sheet with a row list
 * (mobile) / full-screen card grid (tablet/PC) over the immersive overlay,
 * listing every item in the promotion with a compact qty stepper per item.
 * A live madregot steps strip on top shows the cumulative group quantity
 * climbing the price steps — each chip (and its dot on the rail) is anchored
 * at its step's proportional position along the progress bar; reaching a
 * step pops the chip + fires a success haptic.
 *
 * The host (ImmersiveCatalog) owns open/close (Esc + Android back included).
 */
import { computed, ref, watch, onUnmounted } from 'vue'
import { useCatalogStore } from '../../../../app/store/catalog.js'
import { useCartStore } from '../../../../app/store/cart.js'
import {
  getPromoMakats,
  getPromoSteps,
  getNextStepHint,
  computeCumulativeMadregotApplication,
} from '../../../../core/utils/promoCalc.js'
import { VAT_MULTIPLIER } from '../../../../core/config/pricingConstants.js'
import { hapticSuccess } from '../../../../core/utils/haptics.js'
import { playAchievementChime } from '../../../../core/utils/sounds.js'
import ImmersivePromoItemRow from './ImmersivePromoItemRow.vue'

const props = defineProps({
  promo: { type: Object, required: true },
  showPrices: { type: Boolean, default: true },
})

const emit = defineEmits(['close'])

const catalogStore = useCatalogStore()
const cartStore = useCartStore()

const isFirstOrder = computed(() => props.promo.first_order === true)
const isWeekly = computed(() => !isFirstOrder.value && props.promo.weeklyBenefit === true)

// ── Items ────────────────────────────────────────────────────────
const promoItems = computed(() =>
  getPromoMakats(props.promo)
    .map((makat) => catalogStore.itemsByMakat.get(makat))
    .filter(Boolean)
    .filter((item) => item.status !== 'אסור למכירה')
)

// ── Cumulative madregot state (live) ─────────────────────────────
const cartLinesByMakat = computed(() => {
  const result = {}
  for (const line of cartStore.linesArray) {
    result[line.makat] = { qty: line.qty, addedAt: line.addedAt }
  }
  return result
})

const application = computed(() =>
  computeCumulativeMadregotApplication({
    promo: props.promo,
    cartLinesByMakat: cartLinesByMakat.value,
  })
)

const steps = computed(() => getPromoSteps(props.promo))
const totalQty = computed(() => application.value.totalQty)
const activeStepKey = computed(() => application.value.appliedStep?.stepKey || null)
const maxQty = computed(() => application.value.maxQty)
const isOverMax = computed(() => application.value.meta.isOverMax)
const overMaxUnits = computed(() => application.value.meta.overMaxUnits)

const nextHint = computed(() =>
  totalQty.value > 0 ? getNextStepHint(props.promo, totalQty.value) : null
)

const atTopStep = computed(() => {
  if (!steps.value.length || !activeStepKey.value) return false
  return activeStepKey.value === steps.value[steps.value.length - 1].stepKey
})

// Progress toward the highest step (0..1), for the strip's fill bar.
const progressRatio = computed(() => {
  const last = steps.value[steps.value.length - 1]
  if (!last || last.amount <= 0) return 0
  return Math.min(1, totalQty.value / last.amount)
})

// Where a step's checkpoint sits along the rail (0..1 of the top step's qty),
// so each chip + dot lines up with the exact point the fill reaches it.
function stepRatio(step) {
  const last = steps.value[steps.value.length - 1]
  if (!last || last.amount <= 0) return 0
  return Math.min(1, step.amount / last.amount)
}

// Group money total incl. VAT (promo units at the applied step price,
// over-max units at each item's regular price).
const totalWithVat = computed(() => {
  const app = application.value
  if (!app.totalQty) return null
  let sum = 0
  for (const [makat, alloc] of Object.entries(app.allocationsByMakat)) {
    const item = catalogStore.itemsByMakat.get(makat)
    const base = parseFloat(item?.price) || 0
    const unit = app.appliedStep ? app.appliedStep.unitPrice : base
    sum += alloc.promoQty * unit + alloc.regularQty * base
  }
  return (sum * VAT_MULTIPLIER).toFixed(2)
})

// ── Step-reached feedback: chip pop + success haptic ────────────
const poppedKey = ref(null)
let popTimer = null
const appliedPrice = computed(() => application.value.appliedStep?.unitPrice ?? null)

watch(appliedPrice, (nv, ov) => {
  if (nv != null && (ov == null || nv < ov)) {
    hapticSuccess()
    playAchievementChime()
    poppedKey.value = activeStepKey.value
    if (popTimer) clearTimeout(popTimer)
    popTimer = setTimeout(() => {
      poppedKey.value = null
    }, 650)
  }
})

onUnmounted(() => {
  if (popTimer) clearTimeout(popTimer)
})
</script>

<template>
  <div class="imm-pm-backdrop" @click.self="emit('close')">
    <div class="imm-pm-sheet" :class="{ 'is-gold': isFirstOrder }" role="dialog" aria-modal="true">
      <!-- Header -->
      <div class="imm-pm-header">
        <div class="imm-pm-title-wrap">
          <span
            class="imm-pm-badge"
            :class="isFirstOrder ? 'is-gold' : isWeekly ? 'is-blue' : 'is-red'"
          >
            {{ isFirstOrder ? 'מבצע לקוחות חדשים' : isWeekly ? 'הטבה שבועית' : 'מבצע' }}
          </span>
          <span class="imm-pm-title">{{ promo.label || 'מבצע' }}</span>
        </div>
        <button type="button" class="imm-pm-close" aria-label="סגירה" @click="emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Madregot steps strip -->
      <div v-if="showPrices && steps.length" class="imm-pm-stepswrap">
        <div class="imm-pm-steps">
          <div
            v-for="s in steps"
            :key="s.stepKey"
            class="imm-pm-step"
            :style="{ '--pos': stepRatio(s) }"
            :class="{
              reached: totalQty >= s.amount,
              active: s.stepKey === activeStepKey,
              pop: s.stepKey === poppedKey,
            }"
          >
            <span class="imm-pm-step-qty">{{ s.amount }}+ יח'</span>
            <span class="imm-pm-step-price">₪{{ s.price.toFixed(2) }}</span>
          </div>
        </div>

        <!-- Progress toward the top step (fills from the physical right, RTL).
             A checkpoint dot marks each step at the exact spot the fill reaches it. -->
        <div class="imm-pm-progress" aria-hidden="true">
          <div class="imm-pm-progress-fill" :style="{ transform: `scaleX(${progressRatio})` }" />
          <span
            v-for="s in steps"
            :key="s.stepKey"
            class="imm-pm-progress-dot"
            :class="{ reached: totalQty >= s.amount }"
            :style="{ '--pos': stepRatio(s) }"
          />
        </div>

        <!-- Live hint line (reserved height — no jumping while adding) -->
        <div class="imm-pm-hintline">
          <template v-if="nextHint">
            עוד {{ nextHint.unitsNeeded }} יח' למחיר ₪{{ nextHint.nextStep.price.toFixed(2) }}
            ליחידה
          </template>
          <template v-else-if="atTopStep">הגעתם למחיר הטוב ביותר!</template>
          <template v-else-if="maxQty && !nextHint && totalQty > 0"
            >עד {{ maxQty }} יח' במחיר מבצע</template
          >
          <template v-else>הוסיפו פריטים — המחיר יורד ככל שהכמות עולה</template>
        </div>

        <!-- Over-max notice -->
        <div v-if="isOverMax" class="imm-pm-maxnote">
          {{ overMaxUnits }} יח' מעבר למקסימום ({{ maxQty }}) יחויבו במחיר רגיל
        </div>
      </div>

      <!-- Items list -->
      <div class="imm-pm-list">
        <div v-if="promoItems.length === 0" class="imm-pm-empty">אין פריטים זמינים במבצע זה</div>
        <ImmersivePromoItemRow
          v-for="item in promoItems"
          :key="item.makat"
          :item="item"
          :show-prices="showPrices"
        />
      </div>

      <!-- Footer: live totals + done -->
      <div class="imm-pm-footer">
        <div class="imm-pm-totals">
          <span class="imm-pm-total-units">{{ totalQty }} יח' במבצע</span>
          <span v-if="totalWithVat" class="imm-pm-total-money">₪{{ totalWithVat }} כולל מע"מ</span>
        </div>
        <button type="button" class="imm-pm-done" @click="emit('close')">סיום</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.imm-pm-backdrop {
  position: absolute;
  inset: 0;
  z-index: 11;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
}

.imm-pm-sheet {
  width: 100%;
  max-width: 560px;
  max-height: 84dvh;
  display: flex;
  flex-direction: column;
  background: var(--imm-bg-base, #0f172a);
  border-radius: 22px 22px 0 0;
  border-top: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.12));
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ── Header ── */
.imm-pm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
}

.imm-pm-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.imm-pm-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  color: #ffffff;
  white-space: nowrap;
  flex-shrink: 0;
}

.imm-pm-badge.is-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.imm-pm-badge.is-gold {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.imm-pm-badge.is-blue {
  background: var(--color-info);
}

.imm-pm-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--imm-text, #ffffff);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.imm-pm-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.08));
  color: var(--imm-text, #ffffff);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.imm-pm-close svg {
  width: 18px;
  height: 18px;
}

/* ── Steps strip ── */
.imm-pm-stepswrap {
  padding: 4px 18px 10px;
  border-bottom: 1px solid var(--imm-field-border, rgba(255, 255, 255, 0.1));
}

/* The strip is a positioned rail: each chip is anchored at its step's ratio
   along the progress bar underneath (RTL — the fill grows from the physical
   right, so chips anchor from the right too), clamped so edge chips stay
   inside the rail. --chip-w is fixed so the clamp math is exact. */
.imm-pm-steps {
  --chip-w: 64px;
  position: relative;
  height: 42px;
  margin-bottom: 8px;
}

.imm-pm-step {
  position: absolute;
  top: 0;
  width: var(--chip-w);
  right: clamp(0px, calc(var(--pos, 0) * 100% - var(--chip-w) / 2), calc(100% - var(--chip-w)));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 4px 2px;
  border-radius: 12px;
  border: 1.5px solid var(--imm-field-border, rgba(255, 255, 255, 0.14));
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.05));
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;
}

.imm-pm-step-qty {
  font-size: 0.62rem;
  font-weight: 700;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.6));
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.imm-pm-step-price {
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.85));
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.imm-pm-step.reached {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(239, 68, 68, 0.1);
}

.imm-pm-step.reached .imm-pm-step-price {
  color: #ef4444;
}

.imm-pm-step.active {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.18);
  transform: scale(1.06);
  /* Chips can overlap when steps sit close on the rail — active wins */
  z-index: 1;
}

.imm-pm-step.pop {
  animation: imm-pm-step-pop 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes imm-pm-step-pop {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.22);
  }
  100% {
    transform: scale(1.06);
  }
}

/* Gold (first-order) step identity */
.imm-pm-sheet.is-gold .imm-pm-step.reached {
  border-color: rgba(245, 158, 11, 0.55);
  background: rgba(245, 158, 11, 0.1);
}

.imm-pm-sheet.is-gold .imm-pm-step.reached .imm-pm-step-price {
  color: #f59e0b;
}

.imm-pm-sheet.is-gold .imm-pm-step.active {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.18);
}

/* Progress rail */
.imm-pm-progress {
  position: relative;
  height: 4px;
  border-radius: 999px;
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
}

.imm-pm-progress-fill {
  height: 100%;
  width: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #f87171 0%, #dc2626 100%);
  transform-origin: 100% 50%;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.imm-pm-sheet.is-gold .imm-pm-progress-fill {
  background: linear-gradient(90deg, #fcd34d 0%, #d97706 100%);
}

/* Checkpoint dot on the rail, directly under its chip */
.imm-pm-progress-dot {
  position: absolute;
  top: 50%;
  right: calc(var(--pos, 0) * 100%);
  width: 10px;
  height: 10px;
  box-sizing: border-box;
  border-radius: 50%;
  transform: translate(50%, -50%);
  background: var(--imm-bg-base, #0f172a);
  border: 2px solid var(--imm-field-border, rgba(255, 255, 255, 0.25));
  transition:
    background 0.2s ease,
    border-color 0.2s ease;
}

.imm-pm-progress-dot.reached {
  background: #ef4444;
  border-color: #ef4444;
}

.imm-pm-sheet.is-gold .imm-pm-progress-dot.reached {
  background: #f59e0b;
  border-color: #f59e0b;
}

.imm-pm-hintline {
  margin-top: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: center;
  color: #f87171;
  line-height: 1.35;
  min-height: 1.35em;
}

.imm-pm-sheet.is-gold .imm-pm-hintline {
  color: #fbbf24;
}

.imm-pm-maxnote {
  margin-top: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  text-align: center;
  color: var(--imm-warning, #fbbf24);
}

/* ── Items list ── */
.imm-pm-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.imm-pm-empty {
  padding: 32px 16px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.6));
}

/* ── Footer ── */
.imm-pm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid var(--imm-field-border, rgba(255, 255, 255, 0.1));
}

.imm-pm-totals {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.imm-pm-total-units {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.65));
  font-variant-numeric: tabular-nums;
}

.imm-pm-total-money {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--imm-text, #ffffff);
  font-variant-numeric: tabular-nums;
}

.imm-pm-done {
  flex-shrink: 0;
  min-height: 50px;
  padding: 0 34px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
  transition: transform 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-pm-sheet.is-gold .imm-pm-done {
  background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%);
  box-shadow: 0 8px 20px rgba(217, 119, 6, 0.3);
}

.imm-pm-done:active {
  transform: scale(0.95);
}

/* ── Tablet/PC: full-screen takeover with a mobile-app-like card grid.
   The bottom sheet stays mobile-only (<768). ── */
@media (min-width: 768px) {
  .imm-pm-backdrop {
    align-items: stretch;
    padding: 0;
  }

  .imm-pm-sheet {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
    border-top: none;
  }

  .imm-pm-header {
    padding: 20px 28px 10px;
  }

  .imm-pm-title {
    font-size: 1.15rem;
  }

  /* Keep the steps rail + progress at a readable width, centered — chip and
     dot positions stay aligned because both share the same content box. */
  .imm-pm-stepswrap {
    padding: 6px 28px 12px;
  }

  .imm-pm-stepswrap > * {
    max-width: 760px;
    margin-inline: auto;
  }

  .imm-pm-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
    align-content: start;
    width: 100%;
    max-width: 1600px;
    margin-inline: auto;
    padding: 20px 28px 26px;
  }

  .imm-pm-empty {
    grid-column: 1 / -1;
  }

  .imm-pm-footer {
    padding: 14px 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .imm-pm-step,
  .imm-pm-progress-fill,
  .imm-pm-done {
    transition: none;
  }

  .imm-pm-step.pop {
    animation: none;
  }
}
</style>
